import React, { useCallback, useEffect, useState } from "react";
import Stepper from "../../components/Stepper";
import {
  useNavigate,
  useLocation,
  useSearchParams,
  useParams,
} from "react-router-dom";
import {
  IconArrowNarrowLeftDashed,
  IconArrowNarrowRightDashed,
} from "@tabler/icons-react";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import {
  Button,
  Card,
  Form,
  Alert,
  Spinner,
  InputGroup,
  DropdownDivider,
} from "react-bootstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import usePostRequest from "../../hooks/usePostRequest";
import { API_ENDPOINTS } from "../../constant/_constant";
import { toast } from "react-toastify";
import ProductStep1 from "./ProductStep1";
import FileUpload from "../../components/Attachment/Attachment";
import Notes from "../../components/Notes/Notes";
import { transformData, trimTrailingZeros } from "../../utils/_helpers";
import SuccsessMsgModal from "../../components/SuccessMsgModal";
import { useSelector } from "react-redux";
import KitProducts from "./KitProducts";
import useIdleLogout from "../../hooks/useIdleLogout";
import VariantTab from "./VariantTab";
import CvsTab from "./CvsTab";

const generateUniqueId = () => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < 8; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters.charAt(randomIndex);
  }
  const timestamp = Date.now().toString();
  const uniqueId = result + timestamp.slice(-4);
  return uniqueId;
};

// CreateProduct.jsx â€” add near top, after generateUniqueId
const findVehicleById = (list, id) => {
  for (const vehicle of list || []) {
    if (String(vehicle.id) === String(id)) return vehicle;
    if (vehicle.children) {
      const found = findVehicleById(vehicle.children, id);
      if (found) return { ...found, parent: vehicle };
    }
  }
  return null;
};

function CreateProduct() {
  const { sku_id } = useParams();
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formId, setFormId] = useState(null);
  const [selectedOptionSimple, setSelectedOptionSimple] = useState(null);
  const [typeValue, setTypeValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState({
    show: false,
    message: "",
    btntext: "",
    title: "",
  });

  const validationSchema = Yup.object().shape({
    skuId: Yup.string()
      .min(2, "The SKU can not be less than 2.")
      .matches(
        /^[a-zA-Z0-9/ .\-â€“â€”()]+$/,
        "SKU Id can contain only letters, numbers, spaces, /, -, â€“ , â€” and dots",
      )
      .max(255, "The SKU can not be more than 255.")
      .required("SKU is required"),
    name: Yup.string()
      .transform((value) => (value === "" ? null : value))
      .nullable()
      .min(2, "The Product name cannot be less than 2.")
      .max(100, "The Product name cannot be more than 100."),
    description: Yup.string()
      .transform((value) => (value === "" ? null : value))
      .nullable()
      .min(5, "Must be at least 5 characters")
      .max(1000, "Must be at most 1000 characters"),
    salesDescription: Yup.string()
      .transform((value) => (value === "" ? null : value))
      .nullable()
      .min(5, "Must be at least 5 characters")
      .max(1000, "Must be at most 1000 characters"),
    uoi: Yup.string()
      .transform((value) => (value === "" ? null : value))
      .nullable()
      .min(1, "Must be at least 1 characters")
      .max(10, "Must be at most 1000 characters"),
    vendor: Yup.string().required("Vendor is required"),
    // contractvehicle: Yup.string(),
    cost: Yup.number()
      .typeError("Cost must be a valid number")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .notRequired(),
    customer_price: Yup.number()
      .typeError("Customer Price must be a valid number")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .notRequired(),
    // customer_price: Yup.string()
    //   .required("Customer is required")
    //   .matches(/^\d+(\.\d+)?$/, "Customer Price must be a valid number"),
    notes: Yup.array().of(
      Yup.object().shape({
        section: Yup.string().required("Section is required"),
        notesdata: Yup.array().of(
          Yup.object().shape({
            note: Yup.string().required("Note cannot be blank"),
          }),
        ),
      }),
    ),
    variants: Yup.array()
      .of(Yup.object())
      .test(
        "unique-variant-sku",
        "Duplicate variant SKUs are not allowed",
        (list) => {
          if (!list) return true;
          const seen = new Set();
          for (const row of list) {
            const key = row?.variantSku?.trim().toLowerCase();
            if (!key) continue;
            if (seen.has(key)) return false;
            seen.add(key);
          }
          return true;
        },
      )
      .nullable()
      .notRequired(),
    cards: Yup.array().when("$type", {
      is: (val) => val === "kit",
      then: (schema) =>
        schema
          .of(
            Yup.object().shape({
              vendorPartner: Yup.string().nullable(),
              productName: Yup.string().nullable().optional(),
              skuId: Yup.string().nullable(),
              skuDescription: Yup.string().nullable(),
              cost: Yup.number()
                .transform((value, originalValue) =>
                  originalValue === "" || originalValue === null
                    ? undefined
                    : value,
                )
                .typeError("Must be a number.")
                .nullable()
                .notRequired(),
              qty: Yup.number()
                .transform((value, originalValue) =>
                  originalValue === "" || originalValue === null
                    ? undefined
                    : value,
                )
                .typeError("Must be a number.")
                .nullable()
                .notRequired(),
              margin: Yup.number()
                .transform((value, originalValue) =>
                  originalValue === "" || originalValue === null
                    ? undefined
                    : value,
                )
                .typeError("Must be a number.")
                .nullable()
                .notRequired(),
              customerCost: Yup.number()
                .transform((value, originalValue) =>
                  originalValue === "" || originalValue === null
                    ? undefined
                    : value,
                )
                .typeError("Must be a number.")
                .nullable()
                .notRequired(),
              um: Yup.string().nullable(),
            }),
          )
          .nullable()
          .notRequired(),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),

    // cards: Yup.array().when("$type", {
    //   is: (val) => val === "kit",
    //   then: (schema) =>
    //     schema.of(
    //       Yup.object().shape({
    //         vendorPartner: Yup.string().required("Vendor Partner is required."),
    //         skuId: Yup.string()
    //           .min(1, "SKU is required.")
    //           .required("SKU is required."),
    //         skuDescription: Yup.string()
    //           .required("Description is required.")
    //           .min(5, "At least 5 characters."),
    //         cost: Yup.number()
    //           .transform((value) => (value === "" ? null : value))
    //           .typeError("Must be a number.")
    //           .required("Cost is required.")
    //           .test("not-negative", "Must not be negative", (val) => val >= 0),
    //         qty: Yup.number()
    //           .transform((value) => (value === "" ? null : value))
    //           .typeError("Must be a number.")
    //           .required("Qty is required.")
    //           .test("not-negative", "Must not be negative", (val) => val >= 0),
    //         margin: Yup.number()
    //           .transform((value) => (value === "" ? null : value))
    //           .typeError("Must be a number.")
    //           .min(0, "Minimum 0"),
    //         customerCost: Yup.number()
    //           .transform((value) => (value === "" ? null : value))
    //           .typeError("Must be a number.")
    //           .required("Customer cost is required."),
    //         um: Yup.string().required("Unit of Measure is required."),
    //       })
    //     ),
    //   otherwise: (schema) => schema.notRequired().nullable(),
    // }),
  });

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    context: { type: typeValue },
  });

  const { isLoading: createLoading, postData } = usePostRequest(
    API_ENDPOINTS.ADD_SKU,
  );
  const { isLoading: notesLoading, postData: createNotes } = usePostRequest(
    API_ENDPOINTS.CREATE_NOTES,
  );
  const { data: notesData, postData: fetchNotes } = usePostRequest(
    API_ENDPOINTS.GET_NOTES,
  );

  const { data: skuData, postData: fetchSkuData } = usePostRequest(
    API_ENDPOINTS.SINGLE_SKU,
  );
  const { isLoading: updateLoading, postData: updateProduct } = usePostRequest(
    API_ENDPOINTS.UPDATE_SKU,
  );
  const onError = (error) => {
    if (error?.errors) {
      const fieldErrors = Object.values(error?.errors);
      fieldErrors.forEach((err) => {
        if (err?.message) {
          toast.error(err.message);
        }
      });
    } else if (error?.message) {
      toast.error(error.message);
    } else {
      toast.error("An unknown error occurred");
    }
  };

  const {
    handleSubmit,
    setError,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = methods;

  const watchType = watch("Type");
  const maxSteps = 5;
  const steps =
    watchType === "kit"
      ? ["Kit", "Product", "Cvs", "Attachments", "Notes"]
      : ["Product", "Variants", "Cvs", "Attachments", "Notes"];
  useEffect(() => {
    setTypeValue(watchType);
  }, [watchType]);

  const transformVariantRow = (row) => {
    const obj = {};
    if (row?.variantSku) obj.variant_sku = row.variantSku;
    if (row?.internalId) obj.internal_id = row.internalId;
    if (row?.description) obj.description = row.description;
    if (row?.name) obj.variant_name = row.name;
    if (row?.um) obj.uoi = row.um;
    if (row?.salesDescription) obj.description_sales = row.salesDescription;
    if (row?.cost) obj.aspetto_price = Number(row.cost);
    if (row?.customerPrice) obj.customer_price = Number(row.customerPrice);
    return obj;
  };

  const onSubmit = (data) => {
    const transformedProducts = data?.cards?.map((product) => {
      return {
        vendor_name: product?.vendorPartner,
        sku_id: product?.skuIdUnique,
        sku_display_name: product?.skuId,
        name: product?.productName ? product?.productName : null,
        internal_id: product?.internalSku || null,
        variant_id: product?.variantId || null,
        description: product?.skuDescription ? product?.skuDescription : null,
        quantity: product?.qty ? product?.qty : 0,
        raw_sku_id: product?.rowId ? product?.rowId : null,
        aspetto_price: product?.cost ? Number(product?.cost) : 0,
        customer_price: product?.customerCost
          ? Number(product?.customerCost)
          : Number(0),
        margin_percent: product?.margin ? Number(product?.margin) : 0,
        total_price: product?.totalSkuCost ? Number(product?.totalSkuCost) : 0,
        um: product?.um ? product?.um : "EA",
      };
    });

    const validProducts = transformedProducts?.every(
      (p) => p?.sku_display_name && p?.sku_display_name !== "",
    )
      ? transformedProducts
      : [];

    const hasVariant =
      (data?.variants || []).filter((v) => v?.variantSku?.trim()).length > 0;

    const transformedVariants = hasVariant
      ? (data?.variants || []).map(transformVariantRow)
      : null;

    const transformedCvs = Object.values(data?.cvsData || {}).map((cv) => ({
      contract_type_id: cv.id,
      variants: cv.rows.map((row) => ({
        variant_name: row.variantSku,
        internal_id: row.cvSku,
        clin_id: row.clinId,
        raw_sku_id: row.rowId,
      })),
    }));

    const contractTypeIds =
      transformedCvs
        .map((cv) => cv.contract_type_id)
        .filter(Boolean)
        .join(",") || null;

    const payload = {
      attachment_id: formId,
      type: data.Type,
      sku_id: skuData?.data?.sku_id || data.skuId,
      vendor_id: data.vendor_id,
      name: data.name,
      description_sales: data.salesDescription,
      description: data.description,
      uoi: data.uoi,
      kit_type: data.productType?.incomingType || null,
      vendor_sku: data?.vendor_sku || null,
      aspetto_price: parseFloat(data.cost) || 0,
      customer_price: parseFloat(data.customer_price) || 0,
      // contract_type_ids: selectedOptionSimple
      //   ? selectedOptionSimple.map((item) => item.value).join(",")
      //   : null,
      productKits: [...(validProducts ?? [])],
      productKits: [...(validProducts ?? [])],
      has_variant: hasVariant ? 1 : 0,
      variants: transformedVariants,
      contract_vehicles: transformedCvs,
      contract_type_ids: contractTypeIds,
      // productKits: transformedProducts,
    };

    // console.log("payload", payload)

    const onSuccess = (response) => {
      /*
       * Approval: a user who may not write to the library gets the request back
       * instead of a product — the change is applied once it is approved.
       */
      if (response?.data?.applied === false) {
        setIsModalOpen({
          show: true,
          title: sku_id ? "Changes Submitted for Approval" : "Product Submitted for Approval",
          message: response?.message,
          btntext: "View Change Requests",
          primaryTo: "/change-requests",
          newbtn: "Go Home",
          secondaryTo: "/products-list",
        });
        reset();
        return;
      }
      if (response) {
        const notesPayload = {
          module_id: response?.productCreate?.sku_id || skuData?.data?.sku_id,
          module_type: "PRODUCT",
          notes: data.notes,
        };
        // createNotes(notesPayload);
        if (
          Array.isArray(notesPayload.notes) &&
          notesPayload.notes.length > 0
        ) {
          createNotes(notesPayload);
        }
        // toast.success(response.message);
        setIsModalOpen({
          show: true,
          title: `${response?.message}`,
          message: `Prepared by ${user?.username}. SKU is ${response?.sku_id}.`,
          btntext: "Return Home",
        });
        reset();
      }
      // navigate("/products-list");
    };

    if (sku_id) {
      updateProduct(payload, onSuccess, onError);
    } else {
      postData(payload, onSuccess, onError);
    }
  };

  const handleStepChange = useCallback(
    (type) => {
      if (type === "next") {
        const currentData = methods.getValues();
        console.log("currentData", currentData);
        const fieldsToValidate = getFieldsForCurrentStep(currentStep);
        methods.trigger(fieldsToValidate).then((isValid) => {
          if (isValid) {
            if (currentStep <= steps.length - 1) {
              setCurrentStep((prev) => prev + 1);
            }
          } else {
            console.log("Errors:", methods.formState.errors);
          }
        });
      } else {
        console.log("Errors:2", methods.formState.errors);
        if (currentStep > 1) {
          setCurrentStep((prev) => prev - 1);
        }
      }
    },
    [currentStep, methods],
  );

  //   useEffect(() => {
  //     const handleKeyDown = (event) => {
  //       if (event.key === "ArrowRight") {
  //         handleStepChange("next");
  //       } else if (event.key === "ArrowLeft") {
  //         handleStepChange("prev");
  //       }
  //     };

  //     document.addEventListener("keydown", handleKeyDown);
  //     return () => {
  //       document.removeEventListener("keydown", handleKeyDown);
  //     };
  //   }, [handleStepChange]);

  const handleTabChange = (tabIndex) => {
    if (sku_id) {
      const fieldsToValidate = getFieldsForCurrentStep(currentStep);

      methods.trigger(fieldsToValidate).then((isValid) => {
        if (isValid) {
          setCurrentStep(tabIndex);
        } else {
          console.log("Validation failed for step:", currentStep);
        }
      });
    }
  };

  useIdleLogout();

  // CreateProduct.jsx â€” getFieldsForCurrentStep
  const getFieldsForCurrentStep = (step) => {
    switch (step) {
      case 1:
        return ["skuId", "vendor", "cost", "customer_price"];
      case 2:
        return watchType === "kit" ? ["cards"] : ["variants"];
      case 3:
        return ["contractVehicles"];
      default:
        return [];
    }
  };
  useEffect(() => {
    const newFormId = generateUniqueId();
    setFormId(newFormId);
    reset();
  }, []);

  const {
    data: fetchContractdata,
    isLoading: contractLoading,
    postData: fetchContract,
  } = usePostRequest(API_ENDPOINTS.GET_CONTRACT);

  useEffect(() => {
    fetchContract();
  }, []);

  const onSingleNotesSuccess = (res) => {
    if (res?.data) {
      const transformedData = transformData(res.data);
      setValue("notes", transformedData.notes);
    }
  };

  useEffect(() => {
    if (skuData?.data?.sku_id) {
      fetchNotes(
        {
          module_id: skuData?.data?.sku_id,
          module_type: "PRODUCT",
        },
        onSingleNotesSuccess,
      );
    }
  }, [skuData]);

  useEffect(() => {
    if (sku_id) {
      setIsEditing(true);
      const payload = { sku_id };
      fetchSkuData(payload, (response) => {
        if (response?.data) {
          const { data } = response;
          if (data.attachment_id) {
            setFormId(data.attachment_id);
          }
          setValue("vendor_sku", data?.vendor_sku);
          setValue("Type", data?.type);
          setValue("skuId", data?.sku_display_name);
          setValue("skuIdUnique", data?.sku_id);
          setValue("productId", data?.id);
          setValue("name", data?.name);
          setValue("uoi", data?.uoi);
          setValue("description", data?.description);
          setValue("vendor", data?.Vendor?.display_name);
          setValue("vendor_id", data?.Vendor?.id);
          setValue("salesDescription", data?.description_sales);
          setValue("productType", { incomingType: data?.kit_type });
          setValue("variant", data?.has_variant ? "True" : "False");
          setValue(
            "variants",
            (data?.variants || []).map((v) => ({
              variantSku: v?.variant_sku,
              internalId: v?.internal_id,
              name: v?.variant_name,
              um: v?.uoi,
              description: v?.description,
              salesDescription: v?.description_sales,
              cost: v?.aspetto_price,
              customerPrice: v?.customer_price,
            })),
          );

          // const cvRows = data?.contractVehicles || [];
          // const groupedCvRows = cvRows.reduce((acc, row) => {
          //   const key = String(row.contract_type_id);
          //   if (!acc[key]) acc[key] = [];
          //   acc[key].push(row);
          //   return acc;
          // }, {});

          // const cvIds = data?.contract_type_ids
          //   ? data.contract_type_ids
          //       .split(",")
          //       .map((id) => id.trim())
          //       .filter(Boolean)
          //   : Object.keys(groupedCvRows);

          // setValue("contractVehicles", cvIds);

          // const cvsDataPrefill = cvIds.reduce((acc, key) => {
          //   const rowsForCv = groupedCvRows[key] || [];
          //   const vehicle = findVehicleById(fetchContractdata?.data, key);
          //   acc[key] = {
          //     id: key,
          //     name: vehicle?.name,
          //     parentName: vehicle?.parent?.name,
          //     rows: rowsForCv.map((row) => ({
          //       variantSku: row.variant_name,
          //       internalId: row.internal_id,
          //       clinId: row.clin_id,
          //     })),
          //   };
          //   return acc;
          // }, {});

          const variantIdToInternalId = (data?.variants || []).reduce(
            (acc, v) => {
              acc[v.id] = v.internal_id;
              return acc;
            },
            {},
          );

          const cvRows = data?.contractVehicles || [];
          const groupedCvRows = cvRows.reduce((acc, row) => {
            const key = String(row.contract_type_id);
            if (!acc[key]) acc[key] = [];
            acc[key].push(row);
            return acc;
          }, {});

          const cvIds = data?.contract_type_ids
            ? data.contract_type_ids
                .split(",")
                .map((id) => id.trim())
                .filter(Boolean)
            : Object.keys(groupedCvRows);

          setValue("contractVehicles", cvIds);

          const cvsDataPrefill = cvIds.reduce((acc, key) => {
            const rowsForCv = groupedCvRows[key] || [];
            const vehicle = findVehicleById(fetchContractdata?.data, key);
            const rows = rowsForCv.map((row) => ({
              variantInternalId:
                variantIdToInternalId[row.variant_id] || data?.vendor_sku,
              variantSku: row.variant_sku,
              cvSku: row.internal_id,
              clinId: row.clin_id,
              rowId: row.raw_sku_id,
            }));
            const clinValues = rows
              .map((r) => parseInt(r.clinId, 10))
              .filter((n) => !Number.isNaN(n));
            const maxClin = clinValues.length ? Math.max(...clinValues) : 0;
            const baseCount = Math.max(maxClin - rows.length, 0);
            acc[key] = {
              id: key,
              name: vehicle?.name,
              parentName: vehicle?.parent?.name,
              rows,
              baseCount,
            };
            return acc;
          }, {});

          // const cvsDataPrefill = cvIds.reduce((acc, key) => {
          //   const rowsForCv = groupedCvRows[key] || [];
          //   const vehicle = findVehicleById(fetchContractdata?.data, key);
          //   acc[key] = {
          //     id: key,
          //     name: vehicle?.name,
          //     parentName: vehicle?.parent?.name,
          //     rows: rowsForCv.map((row) => ({
          //       variantInternalId: variantIdToInternalId[row.variant_id],
          //       variantSku: row.variant_name,
          //       cvSku: row.internal_id,
          //       clinId: row.clin_id,
          //     })),
          //   };
          //   return acc;
          // }, {});

          setValue("cvsData", cvsDataPrefill);
          setValue("activeCvId", cvIds[0] || null);

          // setValue("cvsData", cvsDataPrefill);
          // setValue("activeCvId", cvIds[0] || null);
          data?.ProductKits.forEach((product, index) => {
            setValue(`cards[${index}].skuDescription`, product?.description);
            setValue(
              `cards[${index}].vendorPartner`,
              product?.Vendor?.display_name,
            );
            setValue(`cards[${index}].internalSku`, product?.internal_id || "");
            setValue(`cards[${index}].variantId`, product?.variant_id || "");
            setValue(`cards[${index}].skuIdUnique`, product?.sku_id);
            setValue(`cards[${index}].skuId`, product?.sku_display_name);
            setValue(`cards[${index}].rowId`, product?.raw_sku_id);
            setValue(
              `cards[${index}].productName`,
              product?.name ?? product?.Product?.name,
            );
            setValue(`cards[${index}].cost`, product?.aspetto_price);
            setValue(`cards[${index}].margin`, product?.margin_percent);
            setValue(
              `cards[${index}].qty`,
              trimTrailingZeros(product?.quantity),
            );
            setValue(`cards[${index}].um`, product?.uoi);
            setValue(`cards[${index}].customerCost`, product?.customer_price);
            setValue(`cards[${index}].totalSkuCost`, product?.total_price);
          });

          setValue("customer_price", data?.customer_price);
          setValue("cost", data?.aspetto_price);
          const selectedIds = data.contract_type_ids.split(",");
          const selectedOptions = fetchContractdata?.data
            .filter((item) => selectedIds.includes(item?.id.toString()))
            .map((item) => ({ label: item?.name, value: item?.id }));
          setSelectedOptionSimple(selectedOptions);
        }
      });
    }
  }, [fetchContractdata, sku_id]);

  const onSingleCopySuccess = (res) => {
    if (res?.data) {
      setValue("Type", res?.data?.type);
      setValue("skuId", res?.data?.sku_display_name);
      setValue("name", res?.data?.name);
      setValue("uoi", res?.data?.uoi);
      setValue("description", res?.data?.description);
      setValue("vendor", res?.data?.Vendor?.display_name);
      setValue("vendor_id", res?.data?.Vendor?.id);
      setValue("salesDescription", res?.data?.description_sales);
      setValue("productType", { incomingType: res?.data?.kit_type });
      res?.data?.ProductKits.forEach((product, index) => {
        setValue(`cards[${index}].skuDescription`, product?.description);
        setValue(
          `cards[${index}].vendorPartner`,
          product?.Vendor?.display_name,
        );
        setValue(`cards[${index}].skuIdUnique`, product?.sku_id);
        setValue(`cards[${index}].skuId`, product?.sku_display_name);
        setValue(`cards[${index}].rowId`, product?.raw_sku_id);
        setValue(
          `cards[${index}].productName`,
          product?.name ?? product?.Product?.name,
        );
        setValue(`cards[${index}].cost`, product?.aspetto_price);
        setValue(`cards[${index}].margin`, product?.margin_percent);
        setValue(`cards[${index}].qty`, trimTrailingZeros(product?.quantity));
        setValue(`cards[${index}].um`, product?.uoi);
        setValue(`cards[${index}].customerCost`, product?.customer_price);
        setValue(`cards[${index}].totalSkuCost`, product?.total_price);
      });

      setValue("customer_price", res?.data?.customer_price);
      setValue("cost", res?.data?.aspetto_price);
      const selectedIds = res?.data.contract_type_ids.split(",");
      const selectedOptions = fetchContractdata?.data
        .filter((item) => selectedIds.includes(item?.id.toString()))
        .map((item) => ({ label: item?.name, value: item?.id }));
      setSelectedOptionSimple(selectedOptions);
    }
  };

  useEffect(() => {
    const productIdDuplication = location.state?.productId;
    if (productIdDuplication) {
      // setValue("orderId", orderIdDuplication || "");
      fetchSkuData({ sku_id: productIdDuplication }, onSingleCopySuccess);
    }
  }, [location.state]);

  return (
    <>
      <SuccsessMsgModal
        show={isModalOpen.show}
        onHide={() =>
          setIsModalOpen(
            { ...isModalOpen, show: false },
            navigate(isModalOpen.primaryTo || "/products-list"),
          )
        }
        title={isModalOpen.title}
        btntext={isModalOpen.btntext}
        message={isModalOpen.message}
        newbtn={isModalOpen.newbtn}
        onSecondaryBtnClick={() =>
          setIsModalOpen(
            { ...isModalOpen, show: false },
            navigate(isModalOpen.secondaryTo || "/products-list"),
          )
        }
      />

      <div className="page-header d-none d-sm-block ">
        <div className=" d-flex justify-content-between  align-items-center">
          <div>
            <h2 className="page-title">
              {sku_id ? "Update Product" : "Create Product"}
            </h2>
          </div>
        </div>
      </div>
      <Card className="pt-3 pb-4 mt-5 mt-sm-0">
        <Card.Body>
          <Stepper
            steps={steps}
            currentStep={currentStep}
            onTabClick={sku_id ? handleTabChange : undefined}
          />
          <FormProvider
            {...methods}
            className=""
            onSubmit={methods.handleSubmit(onSubmit)}
          >
            <Form autoComplete="off" onSubmit={methods.handleSubmit(onSubmit)}>
              {currentStep === 1 && (
                <ProductStep1
                  isEditing={isEditing}
                  selectedOptionSimple={selectedOptionSimple}
                  setSelectedOptionSimple={setSelectedOptionSimple}
                />
              )}
              {currentStep === 2 &&
                (watchType === "kit" ? <KitProducts /> : <VariantTab />)}

              {currentStep === 3 && <CvsTab />}

              {currentStep === 4 && (
                <FileUpload
                  moduleType={"PRODUCT"}
                  attachmnetid={formId}
                  maxFiles={20}
                  maxFileSize={20}
                  onFilesChange={(updatedFiles) => console.log(updatedFiles)}
                />
              )}

              {currentStep === 5 && (
                <Notes module_type={"PRODUCT"} module_id={sku_id} />
              )}

              <div className="d-flex justify-content-center gap-3 mt-3">
                {currentStep > 1 && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => handleStepChange("prev")}
                  >
                    <IconArrowNarrowLeftDashed /> Back
                  </Button>
                )}
                {currentStep < maxSteps && (
                  <Button
                    variant="warning"
                    onClick={() => handleStepChange("next")}
                  >
                    Next <IconArrowNarrowRightDashed />
                  </Button>
                )}

                {sku_id
                  ? currentStep < steps.length && (
                      <div className="d-flex">
                        <Button
                          variant="success"
                          type="submit"
                          isabled={createLoading || updateLoading}
                        >
                          {createLoading || updateLoading ? (
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />
                          ) : (
                            "Update"
                          )}
                        </Button>
                      </div>
                    )
                  : null}

                {currentStep === maxSteps && (
                  <div className="d-flex">
                    <Button
                      variant="warning"
                      type="submit"
                      disabled={createLoading || updateLoading}
                    >
                      {createLoading || updateLoading ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </Form>
          </FormProvider>
        </Card.Body>
      </Card>
    </>
  );
}

export default CreateProduct;
